import { ColumnDef } from "@tanstack/react-table";
import { z, ZodObject, ZodRawShape } from "zod";
import LicensePlate from "license-plate";
import licencePlateSelectBg from "../../../general/fields/modules/commons/licencePlateSelectField/assets/licencePlateSelectBg.svg";
import { useTranslation } from "react-i18next";
import { EventStatus } from "../models/EventModel";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { EventStatusColumn } from "../../modules/features/EventStatusColumn";

export const EventTableColumns: ColumnDef<z.infer<ZodObject<ZodRawShape>>>[] = [
  {
    id: "type",
    accessorKey: "type",
    header: "type",
    cell: ({ row }) => {
      const type = row?.original.type;
      const { t } = useTranslation("common");
      if (type === undefined) return;

      return <>{t("event.type." + type)}</>;
    },
  },
  {
    id: "source",
    accessorKey: "source",
    header: "source",
    enableSorting: false,
    cell: ({ row }) => {
      const { t } = useTranslation("common");

      const source = row?.original.source;

      if (source?.__typename != undefined && source?.__typename == "Tireset") {
        const plate = new LicensePlate(source.car.licence ?? "");

        return (
          <HashLink to={"/car/" + source.car.id + "/edit#tireset-" + source.id}>
            <div className={"d-flex align-items-center gap-2"}>
              {source.car.licence && (
                <div
                  className={"tireset-table-licence-plate"}
                  style={{
                    background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
                  }}
                >
                  <div>
                    {plate.valid() ? plate.pretty() : source.car.licence}
                  </div>
                </div>
              )}
              <div>{source.label}</div>
            </div>
          </HashLink>
        );
      } else if (
        source?.__typename != undefined &&
        source?.__typename == "Request"
      ) {
        const plate = new LicensePlate(source.source.car.licence ?? "");

        return (
          <Link to={"/requests/" + source?.id} target={"_blank"}>
            {t("Request")}

            {plate.valid() ? (
              <div
                style={{
                  display: `inline-block`,
                  marginLeft: 5,
                }}
              >
                <div
                  className={"tireset-table-licence-plate"}
                  style={{
                    background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
                  }}
                >
                  <div>{plate.pretty()}</div>
                </div>
              </div>
            ) : (
              source?.label
            )}
          </Link>
        );
      } else if (source?.label != undefined) {
        return <>{source.label}</>;
      }

      return <>{row.original.raw_source}</>;
    },
  },
  {
    id: "status",
    accessorKey: "status",
    header: "status",
    cell: ({ row }) => {
      const status = row?.original.status;
      const { t } = useTranslation("common");

      if (status === undefined) return;

      let color = "light";
      switch (status) {
        case EventStatus.COMPLETED:
          color = "success";
          break;
        case EventStatus.REJECTED:
          color = "danger";
          break;
        case EventStatus.IN_PROCESS:
          color = "warning";
          break;
        case EventStatus.NEW:
          color = "primary";
          break;
      }

      return (
        <span className={"badge text-bg-" + color}>
          {t("event.state." + status)}
        </span>
      );
    },
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    header: "created_at",
    cell: ({ row }) => {
      const dateString: string = row?.original.created_at;

      if (dateString === undefined || dateString === "" || dateString === null)
        return;

      const utcDate = new Date(dateString);
      const formattedDateTime = new Intl.DateTimeFormat("nl-NL", {
        timeZone: "Europe/Amsterdam",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
        .format(utcDate)
        .replace(/\//g, "-");

      return <span>{formattedDateTime}</span>;
    },
  },
  {
    id: "username",
    accessorKey: "username",
    header: "username",
    cell: ({ row }) => {
      return row?.original.user?.username ?? "-";
    },
  },
  {
    id: "updated_by",
    accessorKey: "updated_by",
    header: "updated_by",
    cell: ({ row }) => {
      return row?.original.updated_by?.username ?? "-";
    },
  },
  {
    id: "message",
    accessorKey: "message",
    header: "message",
  },
  {
    id: "actions",
    accessorKey: "actions",
    header: "actions",
    enableSorting: false,
    cell: ({ row, column }) => {
      return (
        <div>
          <EventStatusColumn
            id={row.original.id}
            status={row?.original.status as EventStatus}
            column={column}
          />
        </div>
      );
    },
  },
];
